import Select from 'react-select';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AppContext } from '../../context';
import { CHATGPTMODULE_OPTIONS, gptTemplateTagsArray } from '../../utils/constant';
import { Button, Col, Form, Label, Row } from 'reactstrap';
import { ChatGptTemplateUpdateDrawerProps, ChatGptTemplateUpdateFormValues } from '../../interfaces';
import { MODULE_ACCESS, SAVE } from '../../constants';
import { DrawerHeader, Sidedrawer } from '../../components/layout/Sidedrawer';
import { customSelectStyleNew } from '../../components/styled/customSelect';
import { useMutation, useQuery } from '@apollo/client';
import { ALL_LLM_MODELS, ALL_LLM_PROVIDERS, SAVE_CHATGPT_TEMPLATES } from './gql';
import { toast } from 'react-toastify';

const DrawerComponent: FC<ChatGptTemplateUpdateDrawerProps> = ({
  isOpen, toggle, data, refetchGPTTemplates
}) => {
  const { register, setValue, handleSubmit, control } =
    useForm<ChatGptTemplateUpdateFormValues>();
  const { theme } = useContext(AppContext);
  const [saveChatgptTemplates, { loading: saveChatgptTemplatesLoading }] = useMutation(SAVE_CHATGPT_TEMPLATES);
  const [copied, setCopy] = useState<boolean>(false);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [description, setDescription] = useState<string>('');

  const { data: providersData } = useQuery(ALL_LLM_PROVIDERS);
  const { data: modelsData } = useQuery(ALL_LLM_MODELS, {
    variables: { providerId: selectedProvider?.value },
    skip: !selectedProvider,
  });

  const providerOptions = useMemo(
    () => (providersData?.allLlmProviders ?? []).map((p: any) => ({
      label: p.displayName,
      value: p.id,
      name: p.name,
    })),
    [providersData]
  );

  const modelOptions = useMemo(
    () => (modelsData?.allLlmModels ?? []).map((m: any) => ({
      label: m.displayName,
      value: m.id,
    })),
    [modelsData]
  );

  const onCopyText = (item: string) => {
    navigator.clipboard.writeText(item);
    setCopy(true);
    setTimeout(() => setCopy(false), 1500);
  };

  const submitData = async (formValues) => {
    const settingsdata = {
      module: formValues?.module?.value,
      template: formValues?.template,
      description: description || undefined,
      modelId: selectedModel?.value || undefined,
    };
    const settingsResp = await saveChatgptTemplates({
      variables: { settingDetails: settingsdata }
    });

    if (settingsResp?.data?.SaveChatgptTemplates?.status === 200) {
      toast.success('LLM Prompt Template saved successfully');
      toggle();
      refetchGPTTemplates();
    }
  };

  useEffect(() => {
    if (!isOpen || !data) return;

    const matchedOption = CHATGPTMODULE_OPTIONS.find(
      (option) => option?.value === data?.module
    );
    if (matchedOption) {
      setValue('module', matchedOption as any);
    }
    setValue('template', data?.template);
    setDescription(data?.description ?? '');

    if (data?.model?.provider) {
      setSelectedProvider({
        label: data.model.provider.displayName,
        value: data.model.provider.id,
      });
    } else {
      setSelectedProvider(null);
    }
    if (data?.model) {
      setSelectedModel({ label: data.model.displayName, value: data.model.id });
    } else {
      setSelectedModel(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, isOpen]);

  return (
    <Sidedrawer toggle={toggle} isOpen={isOpen}>
      <div className="mb-4">
        <DrawerHeader
          loading={saveChatgptTemplatesLoading}
          disabled={saveChatgptTemplatesLoading}
          buttonText={SAVE}
          heading={
            <span>
              {MODULE_ACCESS}
              {data?.originalTemplate && (
                <span className="badge badge-main badge-info ml-2">Default</span>
              )}
            </span>
          }
          prevClick={() => toggle()}
          nextClick={handleSubmit(submitData)}
        />
      </div>
      <div className="pb-4">
        <Form>
          <Row className="mb-3">
            <Col md="12">
              <Label>Name (Module)</Label>
              <Controller
                control={control}
                name="module"
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    isDisabled={true}
                    options={CHATGPTMODULE_OPTIONS as any}
                    isSearchable={false}
                    placeholder="Select Module For Template"
                    onChange={(target) => onChange(target)}
                    styles={{ ...customSelectStyleNew(theme) }}
                  />
                )}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md="6">
              <Label>LLM</Label>
              <Select
                value={selectedProvider}
                options={providerOptions}
                isSearchable={false}
                placeholder="Select LLM"
                onChange={(val) => { setSelectedProvider(val); setSelectedModel(null); }}
                styles={{ ...customSelectStyleNew(theme) }}
              />
            </Col>
            <Col md="6">
              <Label>Model</Label>
              <Select
                value={selectedModel}
                options={modelOptions}
                isSearchable={false}
                isDisabled={!selectedProvider}
                placeholder="Select Model"
                onChange={(val) => setSelectedModel(val)}
                styles={{ ...customSelectStyleNew(theme) }}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md="12">
              <Label>Description</Label>
              <input
                className="form-control"
                placeholder="Brief description of this prompt"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Col>
          </Row>

          <div className="w-100 d-flex flex-wrap mt-2 position-relative">
            {copied && (
              <span className="badge badge-main badge-warning">Copied</span>
            )}
            {gptTemplateTagsArray?.map((item: string, index) => (
              <span
                className="steps-tags-template"
                onClick={() => onCopyText(item)}
                id={`items-${index}`}
                key={index}
              >
                {item.split(', ')}
              </span>
            ))}
          </div>

          <Row>
            <Col md="12">
              <Col>
                <Row md="12" className="d-flex justify-content-between align-items-center">
                  <Label>Prompt Template</Label>
                  <Button
                    className="btn px-3 mb-2 btn-next"
                    onClick={() => setValue('template', data?.originalTemplate || '')}
                  >
                    Original
                  </Button>
                </Row>
              </Col>
              <Controller
                control={control}
                name="template"
                render={({ field }) => (
                  <textarea
                    placeholder="Please add template"
                    rows={10}
                    className="form-control"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    ref={field.ref}
                  />
                )}
              />
            </Col>
          </Row>
        </Form>
      </div>
    </Sidedrawer>
  );
};

export default DrawerComponent;
